// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import approveOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/ApproveOrder";
import deleteOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/DeleteOrder";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: OrderApprovalDetailsActionButtonsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    account: getCurrentAccountState(state)?.value,
    cartPageLink: getPageLinkByPageType(state, "CartPage"),
    orderApprovalListPageLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
    homeLink: getPageLinkByPageType(state, "HomePage"),
});

const mapDispatchToProps = {
    approveOrder,
    deleteOrder,
};

export interface OrderApprovalDetailsActionButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    printButton?: LinkPresentationProps;
    continueShoppingButton?: LinkPresentationProps;
    deleteButton?: LinkPresentationProps;
    approveButton?: LinkPresentationProps;
    printClickable?: ClickableProps;
    continueShoppingClickable?: ClickableProps;
    deleteClickable?: ClickableProps;
    approveClickable?: ClickableProps;
    twoButtonModalStyles?: TwoButtonModalStyles;
}

export const orderApprovalDetailsActionButtonsStyles: OrderApprovalDetailsActionButtonsStyles = {
    deleteButton: linkIconStyleGenerator({
        icon: "Delete",
        textSize: "16px",
        iconSize: 16,
        wrapperCss: css`
            margin-right: 20px;
        `,
    }),
    continueShoppingButton: linkIconStyleGenerator({
        icon: "ChevronLeft",
        textSize: "16px",
        iconSize: 16,
    }),
    approveButton: linkIconStyleGenerator({
        icon: "Check",
        textSize: "16px",
        iconSize: 16,
    }),
    printButton: linkIconStyleGenerator({
        icon: "Printer",
        textSize: "16px",
        iconSize: 16,
        wrapperCss: css`
            margin-right: 20px;
        `,
    }),
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const OrderApprovalDetailsActionButtons = ({
    cart,
    history,
    cartPageLink,
    orderApprovalListPageLink,
    homeLink,
    approveOrder,
    deleteOrder,
    extendedStyles,
    account,
    toaster,
}: Props) => {
    const [styles] = useState(() => mergeToNew(orderApprovalDetailsActionButtonsStyles, extendedStyles));
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [isDisabledApproveButton, setIsDisabledApproveButton] = useState(false);

    if (!cart || !account || !orderApprovalListPageLink || !cartPageLink) {
        return null;
    }

    const approvalDisabled = cart.cartLines?.every(cartline => cartline.isRestricted);

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const continueShoppingClickHandler = () => {
        if (homeLink) {
            history.push(homeLink.url);
        }
    };

    const deleteClickHandler = () => {
        deleteOrder({
            onSuccess: () => {
                toaster.addToast({ body: translate("Order #{0} Deleted", cart.orderNumber), messageType: "success" });
                history.push(orderApprovalListPageLink.url);
            },
        });
    };

    const approveClickHandler = () => {
        setIsDisabledApproveButton(true);

        approveOrder({
            onSuccess: () => {
                history.push(cartPageLink.url);
            },
            onComplete() {
                setTimeout(() => {
                    setIsDisabledApproveButton(false);
                }, 500);
            },
        });
    };

    const printLabel = translate("Print");
    const deleteLabel = translate("Delete Order");
    const approveLabel = translate("Approve Order");
    const continueShoppingLabel = translate("Continue Shopping");

    return (
        <>
            <Hidden {...styles.menuHiddenContainer}>
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <Clickable {...styles.printClickable} onClick={printOrOpenPrintAllModal}>
                        {printLabel}
                    </Clickable>
                    {account.canApproveOrders ? (
                        <>
                            <Clickable {...styles.deleteClickable} onClick={() => setConfirmationModalIsOpen(true)}>
                                {deleteLabel}
                            </Clickable>
                            <Clickable
                                disabled={approvalDisabled || isDisabledApproveButton}
                                {...styles.approveClickable}
                                onClick={approveClickHandler}
                            >
                                {approveLabel}
                            </Clickable>
                        </>
                    ) : (
                        <Clickable {...styles.continueShoppingClickable} onClick={continueShoppingClickHandler}>
                            {continueShoppingLabel}
                        </Clickable>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.buttonsHiddenContainer}>
                <Link
                    data-test-selector="orderApprovalDetails_print"
                    {...styles.printButton}
                    onClick={printOrOpenPrintAllModal}
                >
                    {printLabel}
                </Link>
                {account.canApproveOrders ? (
                    <>
                        <Link
                            data-test-selector="orderApprovalDetails_deleteOrder"
                            {...styles.deleteButton}
                            onClick={() => setConfirmationModalIsOpen(true)}
                        >
                            {deleteLabel}
                        </Link>
                        <Link
                            disabled={approvalDisabled || isDisabledApproveButton}
                            data-test-selector="orderApprovalDetails_approveOrder"
                            {...styles.approveButton}
                            onClick={approveClickHandler}
                        >
                            {approveLabel}
                        </Link>
                    </>
                ) : (
                    <Link
                        data-test-selector="orderApprovalDetails_continueShopping"
                        {...styles.continueShoppingButton}
                        onClick={continueShoppingClickHandler}
                    >
                        {continueShoppingLabel}
                    </Link>
                )}
            </Hidden>
            <TwoButtonModal
                modalIsOpen={confirmationModalIsOpen}
                headlineText={translate("Delete Order")}
                messageText={translate("Do you want to delete order #{0}?", cart.orderNumber)}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                submitTestSelector="confirmDeleteOrderButton"
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={() => setConfirmationModalIsOpen(false)}
                onSubmit={deleteClickHandler}
            ></TwoButtonModal>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withToaster(OrderApprovalDetailsActionButtons)));
